import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FaSearch, FaSpinner, FaEye, FaExternalLinkAlt } from 'react-icons/fa';
import { FileText, Monitor, ListChecks, Tag } from 'lucide-react';
import { useActivityLogs, useTaskExecutionHistory } from '../services/activityService';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useAuth } from 'oidc-react';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { getConditionalStatusBadgeClass, getStatusDisplayText } from '../../common/Utils/color';
import SegmentedControl from '../SegmentedControl/SegmentedControl';
import UtilizationBars from '../UtilizationBars/UtilizationBars';
import { VIEW_MODE_OPTIONS, VIEW_MODE_COLOR_SCHEME, ViewMode, isCompactVisibleStatus } from './patchCenter.config';

interface AdditionalInfoItem {
  AdditionalInfoValueType: string;
  Hostname: string;
  IpAddress: string;
  Status: string;
  OutputLog?: string;
  ErrorMessage?: string;
  RetryAttempts?: number;
  StartTime?: string;
  EndTime?: string;
  Duration?: string;
}

interface UpdateItem {
  id: number;
  type: string;
  activity: string;
  stackTrace: string;
  startTime: string;
  endTime: string;
  status: string;
  cpuUsage: string;
  memoryUsage: string;
  progressReceivedAt: string;
  additionalInfo?: Record<string, AdditionalInfoItem>;
  packageDetail: {
    currentVersion: string | null;
    targetVersion: string | null;
    name: string | null;
    actionType: string | null;
  };
  scriptExecution?: {
    name: string | null;
  };
  registryUpdate?: {
    name: string | null;
    actionType: string | null;
  };
  windowsUpdate?: {
    kbId: string | null;
    title: string | null;
    category: string | null;
    installType: string | null;
  };
  restartAgent?: {
    activity: string | null;
  };
  shutdown?: {
    activity: string | null;
  };
  endpointSync?: {
    activity: string | null;
  };
  shipAgentLogsTask?: {
    activity: string | null;
    startDateTime: string | null;
    endDateTime: string | null;
  };
  taskId?: string;
  agentId?: string;
}

interface TaskHistoryItem {
  id: string;
  activity: string;
  startTime: string;
  endTime: string;
  status: string;
  cpuUsage: string;
  memoryUsage: string;
  progressReceivedAt: string;
  message: string;
  stackTrace: string;
  scriptName?: string;
}

interface DeviceInsightTableProps {
  onHostName?: (hostName: string) => void;
  // When rendered inside a slide-over (e.g. from ReportView) these override the route params.
  remediationPlanIdProp?: string;
  agentIdProp?: string;
  embedded?: boolean;
}

const DeviceInsightTable: React.FC<DeviceInsightTableProps> = ({ onHostName, remediationPlanIdProp, agentIdProp, embedded = false }) => {
  const auth = useAuth();
  const userRole = auth.userData?.profile?.role ?? 'tenantadmin';
  const isGlobalAdmin = userRole?.toLowerCase() === 'globaladmin' || userRole?.toLowerCase() === 'tenantadmin';
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiData, setApiData] = useState<UpdateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [planName, setPlanName] = useState('');
  const [hostName, setHostName] = useState('');
  const [taskCount, setTaskCount] = useState(0);
  const [type, setType] = useState('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<TaskHistoryItem[]>([]);
  const [historyExpandedRow, setHistoryExpandedRow] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<UpdateItem | null>(null);
  const [deviceInstallModalOpen, setDeviceInstallModalOpen] = useState(false);
  const [deviceInstallData, setDeviceInstallData] = useState<AdditionalInfoItem[]>([]);
  const [deviceInstallExpandedRow, setDeviceInstallExpandedRow] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('compact');
  const itemsPerPage = 20;
  const { addToast } = useToast();
  const routeParams = useParams<{ remediationPlanId: string; agentId: string }>();
  const remediationPlanId = remediationPlanIdProp ?? routeParams.remediationPlanId;
  const agentId = agentIdProp ?? routeParams.agentId;
  const navigate = useNavigate();
  const { getActivityLogs } = useActivityLogs();
  const { getTaskExecutionHistory } = useTaskExecutionHistory();

  // Helper function to check if value is valid
  const showValue = (val?: string | null): string => {
    return val && val !== 'NA' && val.trim() !== '' ? val : '';
  };

  const formatDateTime = (value?: string | null): string => {
    if (!value || value.startsWith('0001-01-01')) return 'NA';
    return moment(value).format('DD MMM YYYY, hh:mm A');
  };

  const resolveTaskId = (task: any): string | undefined => {
    return (
      task?.taskId ||
      task?.agentTaskId ||
      task?.remediationPlanTaskId ||
      task?.remediationPlanTaskID ||
      task?.taskExecutionId ||
      task?.taskExecutionID ||
      task?.remediationPlanTaskExecutionId ||
      task?.remediationPlanTaskExecutionID
    );
  };

  // Helper to determine if version is an upgrade or downgrade (simple comparison)
  const compareVersions = (current: string | null, target: string | null): 'upgrade' | 'downgrade' | 'update' => {
    if (!current || !target) return 'update';
    try {
      const currParts = current.split('.').map((p) => parseInt(p, 10));
      const targetParts = target.split('.').map((p) => parseInt(p, 10));
      const maxLen = Math.max(currParts.length, targetParts.length);

      for (let i = 0; i < maxLen; i++) {
        const curr = currParts[i] || 0;
        const tgt = targetParts[i] || 0;
        if (tgt > curr) return 'upgrade';
        if (tgt < curr) return 'downgrade';
      }
      return 'update';
    } catch {
      return 'update';
    }
  };

  // Render activity content based on task type
  const renderActivityContent = (item: UpdateItem): JSX.Element => {
    const { actionType, name, currentVersion, targetVersion } = item.packageDetail;
    const taskType = item.type?.toLowerCase() || 'unknown';

    switch (taskType) {
      case 'softwareinstall': {
        const _name = showValue(name);
        const _target = showValue(targetVersion);

        return (
          <span>
            <span className="font-semibold">Install</span> {_name}
            {_target && <span className="text-green-400"> v{_target}</span>}
          </span>
        );
      }

      case 'softwareupdate': {
        const _name = showValue(name);
        const _current = showValue(currentVersion);
        const _target = showValue(targetVersion);
        const updateDirection = compareVersions(currentVersion, targetVersion);
        const directionLabel = updateDirection === 'upgrade' ? 'Upgrade' : updateDirection === 'downgrade' ? 'Downgrade' : 'Update';

        return (
          <span>
            <span className="font-semibold">{directionLabel}</span> {_name}
            {(_current || _target) && (
              <span className="text-gray-300">
                {' '}
                <span className="text-red-400">{_current}</span>
                <span className="mx-1 text-gray-400">→</span>
                <span className="text-green-400">{_target}</span>
              </span>
            )}
          </span>
        );
      }

      case 'script': {
        const scriptName = showValue(item.scriptExecution?.name);
        return scriptName ? <span>Script: {scriptName}</span> : <span>Script Execution</span>;
      }

      case 'registryupdate': {
        const registryName = showValue(item.registryUpdate?.name);
        const registryAction = showValue(item.registryUpdate?.actionType);

        return (
          <span>
            Registry {registryAction ? registryAction.toUpperCase() : 'Update'}
            {registryName && <span className="text-gray-300"> - {registryName}</span>}
          </span>
        );
      }

      case 'rebootdevice': {
        const restartActivity = showValue(item.restartAgent?.activity);
        return <span>{restartActivity || 'Device Reboot'}</span>;
      }

      case 'windowsosupdate': {
        const updateTitle = showValue(item.windowsUpdate?.title);
        const kbId = showValue(item.windowsUpdate?.kbId);
        const category = showValue(item.windowsUpdate?.category);
        const installType = showValue(item.windowsUpdate?.installType);

        return (
          <span>
            {installType && <span> {installType} - </span>}
            Windows Update <br />
            {kbId && <span> [{kbId}]</span>}
            {updateTitle && <span> - {updateTitle}</span>}
          </span>
        );
      }

      case 'ShutdownDevice': {
        const shutdownActivity = showValue(item.shutdown?.activity);
        return <span>{shutdownActivity || 'System Shutdown'}</span>;
      }

      case 'restartagent': {
        const agentActivity = showValue(item.restartAgent?.activity);
        return <span>{agentActivity || 'Agent Restart'}</span>;
      }

      case 'endpointsync': {
        const syncActivity = showValue(item.endpointSync?.activity);
        return <span>{syncActivity || 'Endpoint Sync'}</span>;
      }

      case 'shipagentlogs': {
        const logsActivity = showValue(item.shipAgentLogsTask?.activity);
        return <span>{logsActivity || 'Ship Agent Logs'}</span>;
      }

      case 'installpatchifiagent': {
        return <span>Install Patchifi Agent</span>;
      }

      case 'getnearbydiscoverabledevices': {
        return <span>Discover Nearby Devices</span>;
      }

      default: {
        const activityMessage = showValue(item.activity);
        return <span>{activityMessage || taskType || 'Unknown Task'}</span>;
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Filter data based on search
  const searchFilteredData = apiData.filter((item) =>
    Object.values(item).some((val) => val != null && val.toString().toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const hasDetailedOnlyData = searchFilteredData.some((item) => !isCompactVisibleStatus(item.status));

  const filteredData = searchFilteredData.filter((item) => (viewMode === 'compact' ? isCompactVisibleStatus(item.status) : true));

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode]);

  const toggleExpand = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const toggleHistoryExpand = (id: string) => {
    setHistoryExpandedRow(historyExpandedRow === id ? null : id);
  };

  const toggleDeviceInstallExpand = (id: string) => {
    setDeviceInstallExpandedRow(deviceInstallExpandedRow === id ? null : id);
  };

  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryData([]);
    setHistoryError(null);
    setSelectedTask(null);
    setHistoryExpandedRow(null);
  };

  const closeDeviceInstallModal = () => {
    setDeviceInstallModalOpen(false);
    setDeviceInstallData([]);
    setDeviceInstallExpandedRow(null);
  };

  const handleOpenHistory = async (task: UpdateItem) => {
    setSelectedTask(task);
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    setHistoryExpandedRow(null);

    const effectiveAgentId = task.agentId || agentId;

    if (!effectiveAgentId || !task.taskId) {
      setHistoryError('Missing identifiers to load execution history.');
      setHistoryData([]);
      setHistoryLoading(false);
      return;
    }

    try {
      const response = await getTaskExecutionHistory(effectiveAgentId, task.taskId);

      const entries: TaskHistoryItem[] = (response?.data || []).map((history: any, idx: number) => {
        const startTime =
          history.taskExecutionStartedAt || history.taskExecutionStartedAtUtc || history.taskExecutionStartTime || history.startDateTime;
        const endTime = history.timestamp || history.taskExecutionEndTime || history.taskExecutionEndedAt || history.endDateTime;

        return {
          id: history.id || `${idx}`,
          activity: showValue(history.taskType) || showValue(history.message) || 'NA',
          startTime: formatDateTime(startTime),
          endTime: formatDateTime(endTime),
          status: showValue(history.status) || 'NA',
          cpuUsage: showValue(history.resourceUsageMetrics?.avgCPUUsage) || 'NA',
          memoryUsage: showValue(history.resourceUsageMetrics?.avgRAMUsage) || 'NA',
          progressReceivedAt: formatDateTime(
            history.taskExecutionStartedAt || history.timestamp || history.taskExecutionStartedAtUtc || history.taskExecutionStartTime,
          ),
          message: history.message || '',
          stackTrace: history.stackTrace || '',
          scriptName: history.scriptExecution?.name || null,
        };
      });

      setHistoryData(entries);
    } catch (error) {
      console.error('Failed to load task execution history:', error);
      setHistoryError('Unable to load execution history right now.');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewDiscoveredDevices = (item: UpdateItem) => {
    if (item.type?.toLowerCase() === 'installpatchifiagent') {
      // Open modal for installation details
      if (item.additionalInfo && Object.keys(item.additionalInfo).length > 0) {
        const devices = Object.values(item.additionalInfo) as AdditionalInfoItem[];
        setDeviceInstallData(devices);
        setDeviceInstallModalOpen(true);
        setDeviceInstallExpandedRow(null);
      }
    } else if (item.type?.toLowerCase() === 'getnearbydiscoverabledevices') {
      const effectiveAgentId = item.agentId || agentId;
      if (effectiveAgentId) {
        navigate(`/deviceManagement/info/${effectiveAgentId}?view=Discovered+Agents`);
      }
    }
  };

  // Add this helper function at the top of the component, after the existing helper functions
  const extractIdFromMessage = (message: string): string | null => {
    // Look for various ID patterns in the message
    const patterns = [
      /(?:under|plan|id|remediation|update)\s*(?:plan|id)?\s*[:\-]?\s*([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i,
      /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i,
      /(?:plan|id)\s*[:\-]?\s*([a-zA-Z0-9\-_]{8,})/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Add this function to handle the navigation
  const handleAlreadySubmittedClick = (item: UpdateItem) => {
    const extractedId = extractIdFromMessage(item.activity);
    const effectiveAgentId = item.agentId || agentId;

    if (!effectiveAgentId) {
      // alert('Unable to navigate: Agent not available');
      addToast('Unable to navigate: Agent not available', 'info');
      return;
    }

    if (!extractedId || extractedId.toLowerCase().includes('unknown')) {
      // alert('Unable to navigate: Remediation Plan ID not found or is unknown');
      addToast('Unable to navigate: Remediation is unknown', 'info');
      return;
    }

    // Navigate to the device insights page
    navigate(`/patchcenter/${extractedId}/DeviceInsights/${effectiveAgentId}`);
  };

  useEffect(() => {
    const fetchLogs = async () => {
      if (!remediationPlanId || !agentId) return;

      try {
        setLoading(true);
        const response = await getActivityLogs(remediationPlanId, agentId);

        if (response?.data?.length > 0) {
          const firstDevice = response.data[0];
          setPlanName(firstDevice.remediationPlanName || 'N/A');
          setHostName(firstDevice.hostName || 'N/A');
          setTaskCount(firstDevice.taskExecutionDetails?.length || 0);
          setType(firstDevice.remediationPlanType || 'N/A');

          if (firstDevice.hostName && onHostName) {
            onHostName(firstDevice.hostName);
          }

          // Flatten tasks
          const mappedData: UpdateItem[] = response.data.flatMap((device: any, deviceIndex: number) =>
            device.taskExecutionDetails.map((task: any, taskIndex: number) => ({
              id: deviceIndex * 100 + taskIndex + 1,
              type: task.taskType || 'unknown',
              activity: task.taskMessage || 'NA',
              stackTrace: task.stackTrace || '',
              startTime: formatDateTime(task.remediationPlanTaskStartTime),
              endTime: formatDateTime(task.remediationPlanTaskEndTime),
              status: task.agentTaskStatus || 'NA',
              cpuUsage: task.cpuUtilizationPercent ? `${task.cpuUtilizationPercent}%` : 'NA',
              memoryUsage: task.ramUsagePercent ? `${task.ramUsagePercent}%` : 'NA',
              progressReceivedAt: formatDateTime(task.remediationPlanTaskProgressLastReceivedAt),
              additionalInfo: task.additionalInfo || undefined,
              packageDetail: {
                currentVersion: task.packageDetail?.currentVersion || null,
                targetVersion: task.packageDetail?.targetVersion || null,
                name: task.packageDetail?.name || null,
                actionType: task.packageDetail?.actionType || null,
              },
              scriptExecution: task.scriptExecution || null,
              registryUpdate: task.registryUpdate || null,
              windowsUpdate: task.windowsUpdate || null,
              restartAgent: task.restartAgent || null,
              shutdown: task.shutdown || null,
              endpointSync: task.endpointSync || null,
              shipAgentLogsTask: task.shipAgentLogsTask || null,
              taskId: resolveTaskId(task) || '',
              agentId: device.agentId || agentId,
            })),
          );

          setApiData(mappedData);
        } else {
          setApiData([]);
        }
      } catch (error) {
        console.error('API call failed:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [remediationPlanId, agentId, onHostName]);

  return (
    <div className={embedded ? 'p-5 text-white' : 'p-6 bg-black text-white'}>
      <div
        className="flex flex-col md:flex-row justify-between gap-5 p-5 rounded-xl border m-1"
        style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText size={15} className="text-gray-500 flex-shrink-0" />
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Remediation Plan</span>
            <span
              className="px-3 py-1 rounded-full text-white text-xs font-medium border"
              style={{ backgroundColor: '#252525', borderColor: '#333' }}
            >
              {planName}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div className="flex items-center gap-2">
              <Monitor size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Host Name</span>
              <span className="text-xs text-gray-200 font-medium">{hostName}</span>
            </div>
            <div className="flex items-center gap-2">
              <ListChecks size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Number of Tasks</span>
              <span className="text-xs text-gray-200 font-medium">{taskCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <Tag size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Type</span>
              <span className="text-xs text-gray-200 font-medium">{type}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between bg-[#1C1C1D] px-3 py-2 rounded text-white mb-3">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            className="bg-[#2A2A2D] text-white border border-[#3A3A40]
             rounded-lg px-4 py-2.5 text-base
             w-80 md:w-96 outline-none
             focus:border-red-500 transition"
          />

          {hasDetailedOnlyData && (
            <div className="flex items-center gap-2">
              <SegmentedControl
                options={VIEW_MODE_OPTIONS}
                value={viewMode}
                onChange={(v) => setViewMode(v as ViewMode)}
                colorScheme={VIEW_MODE_COLOR_SCHEME}
              />
            </div>
          )}
        </div>
      </div>

      <table className="w-full text-sm table-auto border-collapse">
        <thead className="bg-zinc-900">
          <tr className="text-left">
            <th className="p-2">Activity</th>
            <th className="p-2">Start Time</th>
            <th className="p-2">End Time</th>
            <th className="p-2">Status</th>
            <th className="p-2">Utilization</th>
            {currentData.some(
              (item) =>
                (item.type?.toLowerCase() === 'getnearbydiscoverabledevices' || item.type?.toLowerCase() === 'installpatchifiagent') &&
                item.status === 'Completed',
            ) && <th className="p-2 text-center">View Devices</th>}
            <th className="p-2 text-center">Audit</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} className="text-center">
                <div className="flex items-center justify-center py-3 text-gray-400">
                  <FaSpinner className="animate-spin mr-2" size={20} />
                  <span>Loading Remediation Plan info...</span>
                </div>
              </td>
            </tr>
          ) : currentData.length > 0 ? (
            currentData.map((item) => (
              <React.Fragment key={item.id}>
                {/* Main Row */}
                <tr
                  className={`cursor-pointer hover:bg-zinc-700 ${item.id % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'}`}
                  onClick={() => toggleExpand(item.id)}
                >
                  <td className="py-4 px-3">{renderActivityContent(item)}</td>
                  <td className="py-4 px-3">{item.startTime}</td>
                  <td className="py-4 px-3">{item.endTime}</td>
                  <td className="py-4 px-3">
                    <span className={getConditionalStatusBadgeClass(item.status)}>{getStatusDisplayText(item.status)}</span>
                  </td>
                  <td className="py-4 px-3">
                    <UtilizationBars cpu={item.cpuUsage} ram={item.memoryUsage} cpuLabel="CPU Usage" ramLabel="Memory Usage" />
                  </td>
                  {/* View Discovered Devices Button */}
                  {(item.type?.toLowerCase() === 'getnearbydiscoverabledevices' || item.type?.toLowerCase() === 'installpatchifiagent') &&
                    item.status === 'Completed' && (
                      <td className="py-4 px-3 text-center">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-black hover:bg-zinc-700 border border-gray-700 transition-colors duration-200"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleViewDiscoveredDevices(item);
                          }}
                          title={
                            item.type?.toLowerCase() === 'installpatchifiagent' ? 'View installation details' : 'View discovered devices'
                          }
                        >
                          <FaEye className="text-white" size={16} />
                        </button>
                      </td>
                    )}
                  <td className="py-4 px-3 text-center">
                    {/* View History Button */}
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-900 hover:bg-zinc-700 border border-zinc-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenHistory(item);
                      }}
                      title={item.taskId ? 'View' : 'No task available'}
                      disabled={!item.taskId}
                    >
                      <FaSearch />
                    </button>
                  </td>
                </tr>

                {/* Accordion Row with Smooth Transition */}
                <tr>
                  <td colSpan={7} className="p-0">
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedRow === item.id ? 'max-h-40 opacity-100 p-4' : 'max-h-0 opacity-0 p-0'
                      } bg-zinc-700 text-gray-300`}
                    >
                      {item.status === 'AlreadySubmitted' || item.status === 'StaleVersion' ? (
                        <div className="mt-3 p-3 bg-zinc-800 rounded border border-yellow-700">
                          <div className="flex items-center justify-between">
                            <div className="text-sm">
                              <p className=" font-medium mb-1">
                                {item.status === 'StaleVersion' ? 'Superseded by a Newer Remediation Plan' : 'Task Already Submitted'}
                              </p>
                              <p className="text-gray-400 text-xs">{item.activity}</p>
                            </div>
                            <button
                              onClick={() => handleAlreadySubmittedClick(item)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded flex items-center gap-1"
                            >
                              <FaExternalLinkAlt className="w-3 h-3" />
                              View Plan
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <strong>Message:</strong> {item.activity}
                          </div>
                          {item.stackTrace && (
                            <div>
                              <strong>Stack Trace:</strong>
                              {item.stackTrace}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center py-6 text-gray-400">
                No data available.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {filteredData.length > 0 && (
        <div className="flex justify-center mt-4 space-x-2">
          <button
            className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ◀
          </button>
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`px-3 py-1 rounded ${currentPage === idx + 1 ? 'bg-zinc-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
            >
              {idx + 1}
            </button>
          ))}
          <button
            className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            ▶
          </button>
        </div>
      )}

      {/* History Modal - rendered via portal to break out of slide-over stacking context */}
      {historyModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] overflow-hidden">
              <div className="flex items-start justify-between p-4 border-b border-zinc-700">
                <div>
                  <p className="text-lg font-semibold">Audit</p>
                  <div className="text-xs text-gray-400 space-y-1">
                    {historyData.length > 0 && historyData[0].scriptName && (
                      <p>
                        Script: <span className="text-blue-400">{historyData[0].scriptName}</span>
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-gray-300 hover:text-white text-xl leading-none"
                  onClick={closeHistoryModal}
                  aria-label="Close Audit modal"
                >
                  X
                </button>
              </div>

              <div className="p-4 overflow-auto max-h-[70vh] space-y-3">
                {historyError && <div className="bg-red-900 border border-red-700 text-red-100 p-3 rounded">{historyError}</div>}

                {historyLoading ? (
                  <div className="flex items-center justify-center py-6 text-gray-300">
                    <FaSpinner className="animate-spin mr-2" />
                    <span>Loading Audit...</span>
                  </div>
                ) : historyData.length > 0 ? (
                  <table className="w-full text-sm table-auto border-collapse">
                    <thead className="bg-zinc-800">
                      <tr className="text-left">
                        <th className="p-2">Activity</th>
                        <th className="p-2">Start Time</th>
                        <th className="p-2">End Time</th>
                        <th className="p-2">Utilization</th>
                        <th className="p-2">Message</th>
                        <th className="p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((item, idx) => (
                        <React.Fragment key={item.id}>
                          <tr
                            className={`cursor-pointer hover:bg-zinc-700 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'}`}
                            onClick={() => toggleHistoryExpand(item.id)}
                          >
                            <td className="py-3 px-3">{item.activity}</td>
                            <td className="py-3 px-3">{item.startTime}</td>
                            <td className="py-3 px-3">{item.endTime}</td>
                            <td className="py-3 px-3">
                              <UtilizationBars cpu={item.cpuUsage} ram={item.memoryUsage} cpuLabel="CPU Usage" ramLabel="Memory Usage" />
                            </td>
                            <td className="py-3 px-3">
                              <div className="relative">
                                <p className={`${historyExpandedRow === item.id ? '' : 'line-clamp-3'} break-words`}>
                                  {item.message || 'NA'}
                                </p>
                                {item.message && item.message.split('\n').length > 3 && (
                                  <button
                                    type="button"
                                    className="text-blue-400 hover:text-blue-300 text-xs mt-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleHistoryExpand(item.id);
                                    }}
                                  >
                                    {historyExpandedRow === item.id ? 'Show less' : 'Show more'}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <span
                                className={item.status === 'Completed' ? 'text-green-500' : 'text-yellow-400'}
                                title={`Progress Last Received At: ${item.progressReceivedAt}`}
                              >
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center text-gray-400 py-6">No audit list available.</div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Device Installation Details Modal - rendered via portal to break out of slide-over stacking context */}
      {deviceInstallModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] overflow-hidden">
              <div className="flex items-start justify-between p-4 border-b border-zinc-700">
                <div>
                  <p className="text-lg font-semibold">Installation Details</p>
                  <p className="text-xs text-gray-400 mt-1">Device installation results for Patchifi Agent</p>
                </div>
                <button
                  type="button"
                  className="text-gray-300 hover:text-white text-xl leading-none"
                  onClick={closeDeviceInstallModal}
                  aria-label="Close installation modal"
                >
                  X
                </button>
              </div>

              <div className="p-4 overflow-auto max-h-[70vh] space-y-3">
                {deviceInstallData.length > 0 ? (
                  <table className="w-full text-sm table-auto border-collapse">
                    <thead className="bg-zinc-800">
                      <tr className="text-left">
                        <th className="p-2">Hostname</th>
                        <th className="p-2">IP Address</th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Error Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceInstallData.map((device, idx) => (
                        <React.Fragment key={device.Hostname}>
                          <tr
                            className={`cursor-pointer hover:bg-zinc-700 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'}`}
                            onClick={() => toggleDeviceInstallExpand(device.Hostname)}
                          >
                            <td className="py-3 px-3">{device.Hostname}</td>
                            <td className="py-3 px-3">{device.IpAddress}</td>
                            <td className="py-3 px-3">
                              <span className={device.Status === 'Success' ? 'text-green-500 font-semibold' : 'text-red-500 font-semibold'}>
                                {device.Status}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              <div className="relative">
                                <p className={`${deviceInstallExpandedRow === device.Hostname ? '' : 'line-clamp-2'} break-words text-xs`}>
                                  {device.ErrorMessage || 'NA'}
                                </p>
                                {isGlobalAdmin && device.ErrorMessage && device.ErrorMessage.length > 50 && (
                                  <button
                                    type="button"
                                    className="text-blue-400 hover:text-blue-300 text-xs mt-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDeviceInstallExpand(device.Hostname);
                                    }}
                                  >
                                    {deviceInstallExpandedRow === device.Hostname ? 'Show less' : 'Show more'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isGlobalAdmin && deviceInstallExpandedRow === device.Hostname && device.OutputLog && (
                            <tr className="bg-zinc-700">
                              <td colSpan={4} className="p-4">
                                <div className="bg-zinc-800 p-3 rounded border border-zinc-700 max-h-[300px] overflow-auto">
                                  <p className="text-xs font-semibold text-gray-300 mb-2">Output Log:</p>
                                  <pre className="text-xs text-gray-400 whitespace-pre-wrap break-words select-text cursor-text">
                                    {device.OutputLog}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center text-gray-400 py-6">No installation details available.</div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DeviceInsightTable;
